/**
 * ArtifactColumns — DataTable columns for the artifact library. Demo page: /file-manager list (name with type tile,
 * type, owner, size, modified) on /tables/data-tables.
 */
import { Link } from 'react-router-dom';
import { Badge, Chip } from '../../../components/ui/index.js';
import type { DataTableColumn } from '../../../components/ui/index.js';
import type { Artifact, ArtifactSummary } from '../../../generated/api/types.gen.js';
import { formatRelativeTime, formatSize, getKindConfig } from '../artifact-detail/kind-config.js';
import { MEMORY_SCOPE_LABEL, subjectLabel } from '../memory-detail/memory-scope.js';
import { ArtifactKindIcon } from './ArtifactKindIcon.js';

/** List responses carry the full artifact or, for `view=summary`, no content; this page always asks for the full one. */
export type ArtifactListRow = Artifact | ArtifactSummary;

const contentOf = (row: ArtifactListRow): string => ('content' in row ? row.content : '');

function snippet(content: string): string {
  return content
    .replace(/^---\n[\s\S]*?\n---\n/, '')
    .replace(/<[^>]*>/g, '')
    .replace(/#+\s*/g, '')
    .trim()
    .substring(0, 120);
}

export const ARTIFACT_COLUMNS: DataTableColumn<ArtifactListRow>[] = [
  {
    key: 'title',
    header: 'Artifact',
    width: '38%',
    sortable: true,
    sortValue: (row) => row.title.toLowerCase(),
    render: (row) => {
      const deleted = row.deletedAt !== null;
      const text = snippet(contentOf(row));
      return (
        <div className="flex items-center gap-3">
          <ArtifactKindIcon kind={row.kind} />
          <div className="min-w-0">
            {/* A soft-deleted artifact cannot be opened: the server's read route filters it out. */}
            {deleted ? (
              <span className="block font-semibold text-muted line-through">{row.title}</span>
            ) : (
              <Link
                to={`/artifacts/${row.id}`}
                className="block font-semibold text-heading transition-colors hover:text-accent-text"
                onClick={(e) => e.stopPropagation()}
              >
                {row.title}
              </Link>
            )}
            {text && <p className="mt-0.5 line-clamp-1 max-w-[420px] text-xs text-muted">{text}</p>}
          </div>
        </div>
      );
    },
  },
  {
    key: 'kind',
    header: 'Type',
    render: (row) => {
      const kind = getKindConfig(row.kind);
      return <Badge variant={kind.badgeVariant}>{kind.badgeLabel}</Badge>;
    },
  },
  {
    key: 'owner',
    header: 'Belongs to',
    render: (row) => (
      <span className="text-sm text-muted">
        {MEMORY_SCOPE_LABEL[row.scope]} · {subjectLabel(row.subjectType).toLowerCase()}
      </span>
    ),
  },
  {
    key: 'author',
    header: 'Author',
    sortable: true,
    sortValue: (row) => (row.authorName ?? '').toLowerCase(),
    render: (row) => <span className="text-sm text-muted">{row.authorName ?? '—'}</span>,
  },
  {
    key: 'tags',
    header: 'Tags',
    render: (row) => {
      const tags = row.metadata?.tags ?? [];
      if (tags.length === 0) return null;
      return (
        <div className="flex flex-wrap gap-1">
          {tags.map((tag) => (
            <Chip key={tag}>{tag}</Chip>
          ))}
        </div>
      );
    },
  },
  {
    key: 'size',
    header: 'Size',
    align: 'right',
    sortable: true,
    sortValue: (row) => contentOf(row).length,
    render: (row) => <span className="whitespace-nowrap text-sm text-muted">{formatSize(contentOf(row))}</span>,
  },
  {
    key: 'updated',
    header: 'Updated',
    sortable: true,
    sortValue: (row) => new Date(row.updatedAt).getTime(),
    render: (row) =>
      row.deletedAt ? (
        <Badge variant="danger">Deleted</Badge>
      ) : (
        <span className="whitespace-nowrap text-sm text-muted">
          {formatRelativeTime(row.updatedAt)}
          {row.metadata?.version ? ` · v${row.metadata.version}` : ''}
        </span>
      ),
  },
];
